import { computed, effect, Injectable, signal } from '@angular/core';
//TODO: change to interface
import { APIStates, AppService } from 'src/services/app.service';
import { debounceTime, ReplaySubject, Subject, Subscription, takeUntil, throttle } from 'rxjs';
// eslint-disable-next-line max-len
import { EventType, FeatureFlags, FrontendPermissions, IMyEntityInfos, IWFAction, IWFOverviewItem, IWFTicket, NotifyChannel, TWFSync, WFAction, WFTemplate, WFType } from '@vierkant-software/types__api';
import { defaultFilterFilter, workflowCategories } from 'src/app/workflows/workflow-categories.config';
import { DateTime, Duration } from 'luxon';
import { FC_G, GCFormGroup } from '../../ngx-draft';
import { FormBuilder, FormGroup } from '@angular/forms';
import { wfExtraType, wfFormType, WorkflowButtonConfig, WorkflowComponentConfig } from '../workflow.decorator';
import { ActivationEnd, NavigationStart, Router } from '@angular/router';
import { signalMarkAsDirty } from 'src/app/util/signal';

export interface Filter {
  wfTypes: WFType[];
  // TODO @Chris - add more filter options
  showDelayed?: boolean;
  showRecentlyClosed?: boolean;
  sort?: -1 | 0 | 1;
  hideEmpty?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class WorkFlowService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static componentRegistry: Record<string, WorkflowComponentConfig<any>> = {};
  readonly templateNames: Record<string, string> = Object.freeze(Object.fromEntries(Object.entries(WFTemplate)
    .map(([key, value]) => [value as WFTemplate, key])));
  readonly actionNames: Record<string, string> = Object.freeze(Object.fromEntries(Object.entries(WFAction)
    .map(([key, value]) => [value as WFAction, key])));

  //#region: constructor / initializers
  #eventSubscriptions: Subscription;
  #lastSync: DateTime;
  #delayTimer: number;

  constructor(private app: AppService, private router: Router, private fb: FormBuilder) {
    this.app.workflowService = this;
    this.app.translate.stream('WORKFLOW.TYPE').subscribe((x) => {
      this.#WF_TYPE_TRANSLATIONS.set(
        Object.entries(WFType).map(([key, value]) => ({ label: x[key] ?? 'WORKFLOW.TYPE.' + key, value: value as string })),
      );
    });

    this.setupAPIEvents();
    this.setupRouterEvents();
    this.subscribeReconnectHandler();
    this.setupTimer();
  }

  private setupAPIEvents() {
    void this.app.$APIState.forEach(state => {
      switch(state.type) {  // TODO check if this is the correct way to handle all this
        case APIStates.loggedIn:
          this.setupTicketSubscribe().catch(e => console.error("error while subscribing: ", e));
          break;
        case APIStates.externalStudio:
        case APIStates.loggedOut:
          this.#lastSync = undefined;
          this.#ownTickets.set({});
          void this.unsubscribe();
          this.#categories().find(x => x.id === 'filtered').filter = {...defaultFilterFilter},
          signalMarkAsDirty(this.#categories);
          break;
      }
    });
  }

  private setupRouterEvents() {
    this.router.events.subscribe(event => {
      if (this.app.getFeature(FeatureFlags.workflow) === false) return;

      if (event instanceof NavigationStart && this.#currentConfig())
        this.deactivateComponent().catch(console.error);

      if (event instanceof ActivationEnd && event.snapshot.component) {
        if (event.snapshot.params.__IS_WORKFLOW_COMPONENT__)
          this.activateComponent(event.snapshot.params.ticketHandle);
      }
    });
  }

  private subscribeReconnectHandler() {
    this.app.$connectionStatus.subscribe(x => {
      switch(x) {
        case 'connected':
          if (this.#eventSubscriptions)
            this.setupTicketSubscribe().catch(e => console.error("error while reconnecting: ", e));
        break;
      }
    });
  }

  private setupTimer() {
    setInterval(() => {
      if (this.app.state !== APIStates.loggedIn) return;
      if (this.app.getFeature(FeatureFlags.workflow) === false) return;
      try {
        void this.app.api.CommWorker.subscribeToChannel(NotifyChannel.workflowOwn);
        if (this.app.getFrontendPermission(FrontendPermissions.workFlow_orphaned))
          void this.app.api.CommWorker.subscribeToChannel(NotifyChannel.workflowOrphaned);
      } catch(e) {
        console.error("Error while subscribing to workflow sync channels", e);
      }
    }, 1000 * 60 * 60); // every hour

    effect(() => {
      const tickets = this.#ownTickets();
      const now = +DateTime.now();
      const delay = Math.min(
        ...Object.values(tickets).filter(ticket => ticket.personal?.delayTill && +ticket.personal.delayTill > now)
        .map(ticket => +ticket.personal.delayTill - now));
      if (delay > 0 && delay !== Infinity) {
        if (this.#delayTimer) clearTimeout(this.#delayTimer);
        this.#delayTimer = window.setTimeout(() => {
          this.#ownTickets.set({...tickets});
        }, delay + 1000);
      }
    });
  }

  private async setupTicketSubscribe() {
    await this.unsubscribe();
    if (!this.app.getFeature(FeatureFlags.workflow)) return;
    const events = new ReplaySubject(100);
    this.app.$events.subscribe((event) => {
      if (event.data.type === EventType.workflowSync)
        events.next(event.data.data);
    });
    await this.app.api.CommWorker.subscribeToChannel(NotifyChannel.workflowOwn);
    const options = {
      lastSync: this.#lastSync,
      orphaned: false,
    };
    if (this.app.getFrontendPermission(FrontendPermissions.workFlow_orphaned)) {
      options.orphaned = true;
      await this.app.api.CommWorker.subscribeToChannel(NotifyChannel.workflowOrphaned).catch(console.error);
    }
    // console.log('Fetching tickets', options);
    const tickets: Record<string, IWFOverviewItem> = {};
    const [fetched, myEntityInfos] = await Promise.all([
      this.app.api.WorkflowWorker.getTicketList(options).catch((e): IWFOverviewItem[] => { e.displayErrorToast(); return []; }),
      this.app.api.WorkflowWorker.getMyEntityInfos()
        .catch((e): IMyEntityInfos => { e.displayErrorToast(); return { self: [], onBehalfOf: [] }; })
    ]);
    console.log('My entity infos', myEntityInfos);
    this.#myEntityInfos.set(myEntityInfos);
    fetched.forEach(ticket => { tickets[[ticket.ticketId, ticket.responsibilityId].join('.')] = ticket; });
    // const tickets = workflowSystem.mockTickets;
    // TODO merge tickets for reconnect handling
    // console.log('Tickets fetched', tickets);
    this.#ownTickets.set(tickets);
    this.#eventSubscriptions = events.subscribe((event: TWFSync) => {
      console.log('sync', event);

      if (event.type === '$reload') {
        this.#lastSync = undefined;
        void this.setupTicketSubscribe()
          .catch(e => console.error("error while reloading: ", e));
          // TODO think about this - navigate conditions?
          // .then(() => this.router.navigate(['/workflow']));
        return;
      }

      const tickets = {...this.#ownTickets()};
      const handle = [event.ticketId, event.responsibilityId].join('.');
      let ticket: IWFOverviewItem = tickets[handle];
      switch(event.type) {
        case 'update':
          delete event.type;
          ticket = event;
          if (ticket.personal === undefined)
            ticket.personal = tickets[handle]?.personal;

          // entities.some(e => ctrl.some(r => r === e)) ? undefined : entities_obo.filter(e => ctrl.some(r => r === e));
          const onBehalfOfIds = this.#myEntityInfos().self.some(e => ticket.responsibleIds?.some(r => r === e)) ? undefined :
            this.#myEntityInfos().onBehalfOf.filter(e => ticket.responsibleIds?.some(r => r === e));
          console.log('onBehalfOfIds', onBehalfOfIds);
          ticket.personal.onBehalfOfIds = onBehalfOfIds;

          break;
        case 'delete':
          ticket = undefined;
          break;
        case '$delay':
          if (!ticket.personal) ticket.personal = { state: 'unread' };
          ticket.personal.delayTill = event.delayTill;
          break;
        case '$get':
          if (
            this.#currentConfig() && this.#currentTicket() &&
            this.#currentTicket().ticketId === event.ticketId && this.#currentTicket().responsibilityId === event.responsibilityId
          )
            void this.router.navigate(['/workflow']);
          break;
        default:
          console.error('Unknown event type', (event as TWFSync).type); // typecast is sponsored by ts language server...
      }

      tickets[handle] = ticket;
      this.#ownTickets.set(tickets);
      this.#lastSync = ticket.lastModified || ticket.created;
      console.log('lastSync', this.#lastSync);
      // TODO @Chris - show notification
      // TODO process ticket
      const current = {...this.#currentTicket()};
      if (current && current.ticketId === ticket.ticketId && current.responsibilityId === ticket.responsibilityId) {
        current.tempOwnerId = ticket.tempOwnerId;
        this.#currentTicket.set(current);
      }
    });

  }

  async unsubscribe() {
    if (!this.#eventSubscriptions) return;
    this.#eventSubscriptions.unsubscribe();
    await this.app.api.CommWorker.unsubscribeFromChannel(NotifyChannel.workflowOwn);
    await this.app.api.CommWorker.unsubscribeFromChannel(NotifyChannel.workflowOrphaned);
    this.#ownTickets.set({});
  }
  //#endregion

  //#region: signals / states
  #WF_TYPE_TRANSLATIONS = signal<{label: string, value: string}[]>([]);
  #activeCategory = signal<string>("all");
  #categories = signal(workflowCategories);
  #categoriesWithCount = computed(() => {
    return this.#categories().map(category => {
      const tickets = this.filterTickets(category.filter);
      // TODO @chris refine this to whatever fits ;)
      // general remark: make this behaviour configurable?
      const all = tickets.length;
      const prio = tickets.filter(ticket => ticket.priority > 0).length;
      const color: "info" | "danger" | "success" | "warn" = prio > 0 ? 'danger' : undefined;
      const count = all;
      // const count = prio > 0 ? prio : all;
      return { ...category, color, count };
    }).filter(category => category.filter.hideEmpty ?? true ? category.count > 0 : true);
  });
  #ownTickets = signal<Record<string, IWFOverviewItem>>({});
  #myEntityInfos = signal<IMyEntityInfos>({ self: [], onBehalfOf: [] });
  #filter = signal<Filter>({
    wfTypes: [],
  });
  #filteredTickets = computed(() => {
    // console.log('filtering overview', this.#filter());
    const tickets = this.filterTickets(this.#filter());
    tickets.sort((a, b) => (this.#filter().sort ?? -1) * (+a.created - +b.created));
    return tickets;
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  #currentConfig = signal<WorkflowComponentConfig<any>>(undefined);
  #currentTicket = signal<IWFTicket>(undefined);
  #currentForm = signal<FormGroup>(undefined);
  #currentButtons = computed<(WorkflowButtonConfig & { active: boolean })[]>(() => {
    const config = this.#currentConfig();
    const ticket = this.#currentTicket();
    if (!config || !ticket)
      return [];
    return config.buttons
      .filter(x => Object.keys(ticket.actions).includes(x.actionID))
      .map(x => ({...x, active: ticket.actions[x.actionID]}));
  });
  #destroy$: Subject<void> = new Subject<void>();
  //#endregion


  //#region: internal API (overview)
  updateFilter(filter: Filter) {
    this.#categories().find(x => x.id === 'filtered').filter = filter;
    signalMarkAsDirty(this.#categories);
    this.activateCategory('filtered');
  }

  activateCategory(categoryId: string) {
    this.#activeCategory.set(categoryId);
    const category = this.#categories().find(x => x.id === categoryId);
    if (!category) return;
    this.#filter.set(category.filter);
  }

  get activeCategory() {
    return this.#categoriesWithCount().find(x => x.id === this.#activeCategory());
  }

  get WFTypeSelect() {
    return this.#WF_TYPE_TRANSLATIONS();
  }

  get myEntities(): IMyEntityInfos {
    return this.#myEntityInfos();
  }

  get categories() {
    return this.#categoriesWithCount();
  }

  get tickets() {
    return this.#filteredTickets();
  }
  //#endregion
  //#region internal API (ticket)
  get fullForm(): FormGroup {
    return this.#currentForm();
  }

  get buttons() {
    return this.#currentButtons();
  }

  getTicketHandle(ticket: IWFOverviewItem | IWFTicket) {
    return [ticket.ticketId, ticket.responsibilityId].join('.');
  }

  getTicketTemplateID(ticketHandle: string) {
    const [ ticketId, responsibilityId ] = ticketHandle.split('.');
    const ticket = this.#ownTickets()[[ticketId, responsibilityId].join('.')];
    if (!ticket) return;
    return ticket.templateId;
  }

  async ticketLock(ticket = this.#currentTicket()) {
    await this.performAction(WFAction.$lock, ticket, false);
  }

  async ticketUnlock(ticket = this.#currentTicket()) {
    await this.performAction(WFAction.$unlock, ticket, false);
  }

  async ticketAssign(to: string, ticket = this.#currentTicket()) {
    await this.performAction(WFAction.$assign, ticket, false, to);
  }

  async saveNote() {
    //TODO: check if successfull
    await this.performAction(WFAction.$note, this.#currentTicket(), true);
    this.#currentForm().get('note').setValue('');
  }

  async delayBy(until: Duration | DateTime, ticket = this.#currentTicket()) {
    this.#currentForm().get('delay').setValue(until);
    await this.performAction(WFAction.$delay, ticket, true);

    const tickets = {...this.#ownTickets()};
    const overview = tickets[[ticket.ticketId, ticket.responsibilityId].join('.')];
    if (!overview.personal) overview.personal = { state: undefined };
    overview.personal = { ...overview.personal, delayTill: this.makeDateTime(until) };
    this.#ownTickets.set(tickets);

    this.#currentConfig.set(undefined); //remove current config to prevent save on navigate
    void this.router.navigate(['/workflow']);
  }

  async doAction(actionId: WFAction, ticket = this.#currentTicket()) {
    //TODO @Jens/Chris - process result once the backend is updated
    if (!ticket.actions[actionId])
      return;
    await this.performAction(actionId, ticket, true);
    //if successfull
    this.#currentConfig.set(undefined); //remove current config to prevent save on navigate
    void this.router.navigate(['/workflow']);
  }

  async doCheck() {
    return this.performAction(WFAction.$check, this.#currentTicket(), true);
  }

  get isClosed() {
    return this.#currentTicket().finalized !== undefined;
  }
  //#endregion

  //#region internal API (Topbar)
  get topBarCategory() {
    return this.#categoriesWithCount().find(x => x.id === 'all');
  }
  //#endregion

  //#region public API
  getTicketURL(ticket: IWFOverviewItem | IWFTicket): string[] {
    return ['/workflow', 'ticket', this.getTicketHandle(ticket)];
  }

  getForm<T>(): FC_G<wfFormType<T>> {
    return this.#currentForm().controls.data as FC_G<wfFormType<T>>;
  }

  getExtra<T>(): wfExtraType<T>{
    return this.#currentTicket().extra as wfExtraType<T>;
  }

  get currentTicketTemplate() {
    return 'WORKFLOW.' + this.templateNames[this.#currentTicket().templateId];
  }

  get ticketData() {
    return this.#currentTicket();
  }
  //#endregion

  //#region private helpers

  private makeDateTime(delay: DateTime | Duration) {
    if (DateTime.isDateTime(delay))
      return delay;
    if (Duration.isDuration(delay))
      return DateTime.now().plus(delay);
    return undefined;
  }

  private async performAction(actionId: WFAction, ticket: IWFTicket, addForm: boolean, userId?: string) {
    console.log('Performing action - #currentForm:', this.#currentForm().value);
    let { data: formData, note, delay, onBehalfOf } = this.#currentForm().value;
    if (!addForm) formData = undefined;
    const delayTill = actionId === WFAction.$delay ? this.makeDateTime(delay) : undefined;

    const action = { actionId, formData, note, delayTill } as IWFAction;
    if (action.actionId === WFAction.$assign && userId) action.userId = userId;

    const response = await this.app.api.WorkflowWorker.executeAction(
      ticket.ticketId,
      action,
      {
        responsibilityId: ticket.responsibilityId,
        onBehalfOfId:     onBehalfOf || undefined,  // TODO @chris - please avoid setting it to null
      }
    ).catch(e => e.displayErrorToast());

    // or do we only want to update the buttons?
    if (actionId === WFAction.$check) {
      if (response?.ticket &&
        this.#currentTicket() &&
        this.#currentTicket().ticketId === response.ticket.ticketId &&
        this.#currentTicket().responsibilityId === response.ticket.responsibilityId
      )
        this.#currentTicket.set(response.ticket);
    }
  }

  private filterTickets(filter: Filter) {

    const now = +DateTime.now();
    return Object.values(this.#ownTickets())
      .filter((ticket: IWFOverviewItem) => !!ticket.templateId)
      .filter((ticket: IWFOverviewItem) => ticket.finalized === undefined || filter.showRecentlyClosed)
      .filter((ticket: IWFOverviewItem) => filter.wfTypes.length === 0 || filter.wfTypes.includes(ticket.workflowId))
      .filter((ticket: IWFOverviewItem) => filter.showDelayed || ticket.personal?.delayTill === undefined || +ticket.personal.delayTill < now);
  }
  //#endregion

  //#region state tracking
  activateComponent(ticketHandle: string) {
    const templateId = this.getTicketTemplateID(ticketHandle);
    const config = WorkFlowService.componentRegistry[templateId];
    if (!config) {
      //TODO: proper error page?
      console.error('No component config found for templateId', templateId);
      void this.router.navigate(['/workflow']);
      return;
    }
    this.#currentConfig.set(config);
    const currentGCForm = new GCFormGroup<unknown>({
      note:       [""],
      delay:      [],
      data:       config.formGroup,
      onBehalfOf: [""],
    });

    // default it from meta or use 1st onBehalfOfId
    let onBehalfOf = this.#currentTicket().onBehalfOfId;
    if (!onBehalfOf && !this.#currentTicket().isOwn) onBehalfOf = this.#currentTicket().onBehalfOfIds?.[0];

    this.#currentForm.set(currentGCForm.getFormGroup(this.fb, {
      note: this.#currentTicket().note,
      data: this.#currentTicket().formData,
      onBehalfOf,
    }));

    if (this.#currentTicket().finalized !== undefined)
      this.#currentForm().disable();

    const throttleSubs = new Subject<void>();
    this.#currentForm().valueChanges.pipe(takeUntil(this.#destroy$)).subscribe(() => signalMarkAsDirty(this.#currentForm));
    this.#currentForm().valueChanges.pipe(
        takeUntil(this.#destroy$),
        debounceTime(250),
        throttle(() => throttleSubs, { leading: true, trailing: true})
      )
      .subscribe((value) => {
        void this.performAction(WFAction.$check, this.#currentTicket(), true)
          .finally(() => throttleSubs.next());
    });
  }

  async deactivateComponent() {
    this.#currentConfig.set(undefined);
    this.#destroy$.next();
  }

  async openTicket(ticketHandle: string) {
    const overview = this.#ownTickets()[ticketHandle]; //TODO: strip OBHO
    if (!overview) return;

    const response = await this.app.api.WorkflowWorker.executeAction(overview.ticketId,
      { actionId: WFAction.$get } as IWFAction,
      {
        responsibilityId: overview.responsibilityId,
        // onBehalfOfId:     overview.personal.onBehalfOf?.ID
      }
    ).catch((error) => {
      console.error("Error while opening ticket", ticketHandle, error);
      // TODO @Chris - show error message
      // we need to throw an error here to prevent the ticket from being opened
      throw new Error("Error while opening ticket");
    });

    if (!response) return;

    // mark ticket as read
    if (overview.personal.state === 'unread') {
      const tickets = {...this.#ownTickets()};
      overview.personal = { ...overview.personal, state: undefined };
      this.#ownTickets.set(tickets);
    }

    this.#currentTicket.set(response.ticket);
    //TODO: if same templateId, update form directly
  }
  //#endregion
}
